import React from 'react';
import { Card, Typography } from 'antd';
import { AspectRatio } from '../constants/clients/template-config.interface';
import { useResponsiveSizes } from '../hooks/use-responsive-sizes';

const { Text } = Typography;

interface TemplateCardProps {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  image?: string;
  video?: string;
  aspectRatio: AspectRatio;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({
  title,
  onClick,
  disabled,
  image,
  video,
  aspectRatio = 'vertical'
}) => {
  const { fontSize, screens } = useResponsiveSizes();
  const [showVideo, setShowVideo] = React.useState(false);
  const [videoLoaded, setVideoLoaded] = React.useState(false);
  const hoverTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  const imageHeight = screens.xs ? 220 : screens.sm ? 270 : screens.md ? 320 : 370;
  const borderRadius = 14;

  const getImageDimensions = () => {
    if (aspectRatio === 'horizontal') {
      const width = imageHeight * (16 / 9);
      return { width: `${width}px`, height: `${imageHeight}px` };
    } else {
      const width = imageHeight * (9 / 16);
      return { width: `${width}px`, height: `${imageHeight}px` };
    }
  };

  const dimensions = getImageDimensions();

  // Precargar el video cuando el componente se monta
  React.useEffect(() => {
    if (disabled && video && videoRef.current) {
      videoRef.current.load();
    }
  }, [disabled, video]);

  const handleMouseEnter = () => {
    if (disabled && video) {
      hoverTimeoutRef.current = setTimeout(() => {
        setShowVideo(true);
        if (videoRef.current && videoLoaded) {
          videoRef.current.currentTime = 0;
          videoRef.current.play().catch(() => {
            console.warn('Error al reproducir video');
          });
        }
      }, 300);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    if (showVideo) {
      setShowVideo(false);
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  };

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
  };

  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <style>
        {`
          .card-animate {
            transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease;
          }
          .card-animate:hover {
            transform: scale(1.02) translateY(-4px);
            box-shadow: 0 8px 16px rgba(0,0,0,0.12);
          }
        `}
      </style>

      <div
        style={{
          width: dimensions.width,
          minWidth: dimensions.width,
          flexShrink: 0,
          marginBottom: 20
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Card
          hoverable={!disabled}
          onClick={disabled ? undefined : onClick}
          className={disabled ? '' : 'card-animate'}
          style={{
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? 'default' : 'pointer',
            width: dimensions.width,
            borderRadius: borderRadius
          }}
          styles={{
            body: {
              display: 'flex',
              flexDirection: 'column',
              padding: '0'
            }
          }}
        >
          <div
            style={{
              height: dimensions.height,
              borderRadius: borderRadius,
              overflow: 'hidden',
              backgroundColor: '#000000',
              position: 'relative'
            }}
          >
            {/* Imagen siempre visible, el video aparece encima */}
            <img
              src={image}
              alt='Preview'
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: borderRadius
              }}
            />

            {/* Video precargado pero oculto hasta hover */}
            {disabled && video && (
              <video
                ref={videoRef}
                src={video}
                loop
                muted
                playsInline
                preload='auto'
                onCanPlayThrough={handleVideoLoaded}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: borderRadius,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  opacity: showVideo && videoLoaded ? 1 : 0,
                  transition: 'opacity 0.2s ease',
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: '15px' }}>
          <Text
            strong
            style={{
              fontSize: fontSize.lg,
              fontWeight: 400,
              letterSpacing: '1px',
              color: '#0c0615'
            }}
          >
            {title}
          </Text>
        </div>
      </div>
    </>
  );
};
